// Types for SMM logo placement — shared by StaticImage posts
// (GET/POST /static-post/{post_id}/logo-corners, apply-logo) and a Carousel,
// where every slide's corner is independent
// (GET/POST /content-generation/carousel/{post_id}/logo-corners, apply-logo,
// both require slide_number).

export type LogoCorner = 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right';
export type LogoPosition = LogoCorner | 'none';

export const LOGO_CORNERS: LogoCorner[] = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];

export const LOGO_POSITION_LABELS: Record<LogoPosition, string> = {
  top_left: 'Top left',
  top_right: 'Top right',
  bottom_left: 'Bottom left',
  bottom_right: 'Bottom right',
  none: 'No logo',
};

export interface LogoCornerScore {
  verdict: 'clean' | 'busy';
  edge_density: number;
}

export interface LogoCornersResponse {
  has_logo: boolean;
  logo_url?: string | null;
  logo_position: LogoPosition | null;
  corners: Record<LogoCorner, LogoCornerScore> | null;
  recommended: LogoCorner | null;
  /** The background-removed version of the logo, when the profile has one. */
  logo_transparent_url?: string | null;
  /** How the logo is currently placed on this post (or slide). */
  logo_style?: LogoStyle;
  /** The original logo comes with its own background. */
  logo_has_background?: boolean;
  /** Brand colours (#RRGGBB) offered as logo backgrounds. */
  brand_colors?: string[];
}

export type LogoSize = 'small' | 'medium' | 'large';

/** How the logo is placed: which logo, its size, a background box behind it, rounding. */
export interface LogoStyle {
  variant: 'original' | 'transparent';
  /** #RRGGBB box behind the logo; null = no box. */
  background: string | null;
  /** Rounds the box — or, with no box, the logo's own corners. */
  rounded: boolean;
  /** Logo size on the image; medium when unset (posts placed before sizes existed). */
  size?: LogoSize;
}

export const DEFAULT_LOGO_STYLE: LogoStyle = { variant: 'original', background: null, rounded: false, size: 'medium' };

export function sameLogoStyle(a: LogoStyle, b: LogoStyle): boolean {
  return (
    a.variant === b.variant &&
    (a.background ?? '').toUpperCase() === (b.background ?? '').toUpperCase() &&
    a.rounded === b.rounded &&
    (a.size ?? 'medium') === (b.size ?? 'medium')
  );
}

/** Geometry matching the engine's logo compositor (shared/logo_compositor.py):
 * box padding as a fraction of the logo height, box corner radius as a
 * fraction of the box height, and the logo's own corner radius (no box). */
export const LOGO_BOX_PADDING_RATIO = 0.12;
export const LOGO_BOX_RADIUS_RATIO = 0.2;
export const LOGO_CORNER_RADIUS_RATIO = 0.2;
/** Gap from the box's outer edge to the image edge, fraction of the image width (both axes). */
export const LOGO_MARGIN_RATIO = 0.03;

/** Logo width and max height as fractions of the image width / height (LOGO_SIZES in the compositor). */
export const LOGO_SIZES: Record<LogoSize, { label: string; widthRatio: number; maxHeightRatio: number }> = {
  small: { label: 'Small', widthRatio: 0.1, maxHeightRatio: 0.09 },
  medium: { label: 'Medium', widthRatio: 0.14, maxHeightRatio: 0.12 },
  large: { label: 'Large', widthRatio: 0.2, maxHeightRatio: 0.17 },
};

/** The subset of the backend's WorkspacePostView that apply-logo callers use. */
export interface ApplyLogoResponse {
  post_id: string;
  image_url?: string | null;
  raw_image_url?: string | null;
  logo_position?: LogoPosition | null;
  image_gen_status?: string | null;
  /** Carousel only — the affected slide's composited image already lives at
   * its own index; image_url above only ever mirrors slide 1. */
  carousel_slide_urls?: string[] | null;
  /** Carousel only — index-aligned with carousel_slide_urls. */
  raw_carousel_slide_urls?: (string | null)[] | null;
  carousel_logo_positions?: (LogoPosition | null)[] | null;
  [key: string]: unknown;
}
